import { IoClose } from 'react-icons/io5';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useEffect, useRef, useState } from 'react';
import { CashbackRedeemTab } from './cashback/cashback-redeem-tab';
import { useCashback } from '@/services/cashback.service';
import { CashbackHistoryTab } from './cashback/cashback-history-tab';
import { CashbackTransactionTab } from './cashback/cashback-transaction-tab';

type CashbackModalProps = {
  open: boolean;
  closeModal: () => void;
};

const CashbackModal = ({ open, closeModal }: CashbackModalProps) => {
  const [activeTab, setActiveTab] = useState<string>('redeem');
  const tabsListRef = useRef<HTMLDivElement | null>(null);
  const underlineRef = useRef<HTMLDivElement | null>(null);
  const { getAllCashback } = useCashback();

  // Fetch cashback data when modal opens
  useEffect(() => {
    if (open) {
      getAllCashback();
    }
  }, [open, getAllCashback]);

  // Tab indicator slide animation
  useEffect(() => {
    const updateUnderline = () => {
      if (!tabsListRef.current || !underlineRef.current) return;

      const activeEl = tabsListRef.current.querySelector<HTMLElement>('[data-state="active"]');
      if (!activeEl) return;

      const underline = underlineRef.current;
      const { offsetLeft, offsetWidth } = activeEl;

      underline.style.width = `${offsetWidth}px`;
      underline.style.transform = `translateX(${offsetLeft}px)`;
    };

    // Delay to ensure DOM is ready when modal opens
    const timeoutId = setTimeout(updateUnderline, 0);
    window.addEventListener('resize', updateUnderline);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateUnderline);
    };
  }, [activeTab, open]);

  return (
    <Dialog open={open} onOpenChange={closeModal}>
      <DialogContent className="max-w-[76rem] h-auto p-0 shadow-lg dark:bg-[#1A1A1A] !border-none !rounded-[1.5rem] bg-[#F6F6F6] flex flex-col overflow-hidden">
        <div className="relative">
          <div className="w-full h-full flex flex-col min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between px-[2.4rem] py-[1.8rem] flex-shrink-0">
              <h2 className=" dark:text-[#DEDEDE] text-[#272727] text-[3.4rem] font-medium">
                Cashback
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="text-[#57E8BA] hover:text-[#4dd4a7] transition-colors absolute top-[1rem] right-[1rem]"
              >
                <IoClose className="size-10" />
              </button>
            </div>
          </div>
          <div className="absolute w-full top-0 dark:bg-[radial-gradient(50%_7778.57%_at_50%_50%,_#57E8BA_0%,_#161616_71.15%)] h-[3px] bg-[radial-gradient(50%_7778.57%_at_50%_50%,_#4FD3A9_0%,_#F5F5F5_71.15%)]" />
          <div>
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v)} defaultValue="redeem">
              <TabsList
                ref={tabsListRef}
                className="relative !bg-transparent border-b dark:border-[#717171] border-[#BCBCBC] w-full pb-[1.5rem] h-auto justify-start px-[2.4rem]"
              >
                <TabsTrigger
                  value="redeem"
                  className="!bg-transparent font-medium text-[1.8rem] tracking-[0] text-center block py-[0] dark:data-[state=active]:text-[#57E8BA] data-[state=active]:text-[#121212] dark:text-[#929292] text-[#BBBBBB] !shadow-none"
                >
                  Redeem
                </TabsTrigger>
                <TabsTrigger
                  value="history"
                  className="!bg-transparent font-medium text-[1.8rem] tracking-[0] text-center block py-[0] dark:data-[state=active]:text-[#57E8BA] data-[state=active]:text-[#121212] dark:text-[#929292] text-[#BBBBBB] !shadow-none"
                >
                  History
                </TabsTrigger>
                <TabsTrigger
                  value="transaction"
                  className="!bg-transparent font-medium text-[1.8rem] tracking-[0] text-center block py-[0] dark:data-[state=active]:text-[#57E8BA] data-[state=active]:text-[#121212] dark:text-[#929292] text-[#BBBBBB] !shadow-none"
                >
                  Transaction
                </TabsTrigger>

                {/* Tab indicator underline */}
                <div
                  ref={underlineRef}
                  className="pointer-events-none absolute bottom-[-0.2rem] left-0 h-[0.2rem] dark:bg-[#57E8BA] bg-[#121212] transition-all duration-300 ease-out"
                />
              </TabsList>
              <div className=" px-[2.4rem] pb-[2rem]">
                <TabsContent value="redeem" className="mt-[3.4rem]">
                  <CashbackRedeemTab closeModal={closeModal} />
                </TabsContent>
                <TabsContent value="history">
                  <CashbackHistoryTab closeModal={closeModal} />
                </TabsContent>
                <TabsContent value="transaction" className="mt-[1.5rem]">
                  <CashbackTransactionTab closeModal={closeModal} />
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CashbackModal;
